import { alert, confirmDelete, ModalSize } from '@ardoq/modal';
import { deleteComponentsByIds, getDialogConfigByIds } from './helpers';
import { logError } from '@ardoq/logging';
import { dispatchAction } from '@ardoq/rxbeach';
import { deleteReferences } from 'streams/references/ReferenceActions';
import { isScenarioMode } from 'models/utils/scenarioUtils';
import { getManagedDialogConfig } from 'externallyManaged/confirmManagedComponentDeletion/getDialogConfig';
import { ArdoqId, ProvenanceSource } from '@ardoq/api-types';
import { trackDialogDeleteButton } from '../tracking';
import {
  componentInterface,
  IsExternallyManaged as IsExternallyManagedComponent,
} from '@ardoq/component-interface';
import {
  referenceInterface,
  IsExternallyManaged as IsExternallyManagedReference,
} from '@ardoq/reference-interface';
import {
  isArdoqError,
  ArdoqError,
  RequireAtLeastOne,
} from '@ardoq/common-helpers';
import { contextInterface } from 'modelInterface/contextInterface';
import { TrackingLocation } from '@ardoq/data-model';
import { Trans, t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { activeView$ } from 'streams/views/mainContent/activeView$';

export type DataInterface = {
  isComponentExternallyManaged: IsExternallyManagedComponent;
  isReferenceExternallyManaged: IsExternallyManagedReference;
  getCurrentContextComponentId: () => ArdoqId | null;
  setContextComponentById: (
    componentId: string,
    trackingLocation?: TrackingLocation
  ) => Promise<void>;
};

type ComponentsAndOrReferencesIds = RequireAtLeastOne<
  {
    componentIds?: ArdoqId[];
    componentNames?: string[];
    referenceIds?: ArdoqId[];
  },
  'componentIds' | 'referenceIds'
> & {
  isExternallyManagedNoticeSuppressed: boolean;
  getNextTreeSelectionCandidate?: () => ArdoqId | null;
  shouldReturnComponentBatchDeleteResponse?: boolean;
  onConfirmedCallback?: () => void;
  dataInterface?: DataInterface;
  provenanceSource?: ProvenanceSource;
};

const defaultDataInterface: DataInterface = {
  isComponentExternallyManaged: componentInterface.isExternallyManaged,
  isReferenceExternallyManaged: referenceInterface.isExternallyManaged,
  getCurrentContextComponentId: contextInterface.getCurrentComponentId,
  setContextComponentById: contextInterface.setComponentById,
};

export const launchDeleteComponentsAndReferencesConfirmationDialogByIds =
  async ({
    componentIds,
    componentNames,
    referenceIds,
    isExternallyManagedNoticeSuppressed,
    getNextTreeSelectionCandidate = () => null,
    shouldReturnComponentBatchDeleteResponse,
    onConfirmedCallback,
    dataInterface = defaultDataInterface,
    provenanceSource,
  }: ComponentsAndOrReferencesIds) => {
    const componentIdList = componentIds ?? [];
    const referenceIdList = referenceIds ?? [];

    const externallyManagedComponentsCount = componentIdList.filter(
      dataInterface.isComponentExternallyManaged
    ).length;

    const externallyManagedReferencesCount = referenceIdList.filter(
      dataInterface.isReferenceExternallyManaged
    ).length;

    const shouldShowManagedDialog =
      (externallyManagedComponentsCount || externallyManagedReferencesCount) &&
      !isExternallyManagedNoticeSuppressed;

    const confirmed = await confirmDelete(
      shouldShowManagedDialog
        ? getManagedDialogConfig({
            numOfComponents: externallyManagedComponentsCount,
            numOfReferences: externallyManagedReferencesCount,
          })
        : await getDialogConfigByIds(
            componentIdList,
            referenceIdList,
            componentNames
          )
    );

    if (confirmed) {
      onConfirmedCallback?.();
      // eslint-disable-next-line ardoq-lint/no-direct-stream-state-access -- imperative one-shot confirm-delete dialog opener; activeView is read once at click time
      trackDialogDeleteButton(activeView$.state.mainViewId); // the only way to reach this line is if the delete button was clicked
      dispatchAction(
        deleteReferences({
          referenceIds: referenceIdList,
          isScenarioMode: isScenarioMode(),
          provenanceSource,
        })
      );
      const currentContextComponentId =
        dataInterface.getCurrentContextComponentId();
      const nextTreeSelectionCandidate = getNextTreeSelectionCandidate();
      if (
        nextTreeSelectionCandidate &&
        currentContextComponentId &&
        componentIdList.includes(currentContextComponentId)
      ) {
        dataInterface.setContextComponentById(nextTreeSelectionCandidate);
      }
      const components = await deleteComponentsByIds(
        componentIdList,
        provenanceSource
      );
      if (isArdoqError(components)) {
        handleError(components);
        return false;
      }
      if (shouldReturnComponentBatchDeleteResponse && components)
        return components;
    }
    return confirmed;
  };

const handleError = (error: ArdoqError) => {
  alert({
    title: 'Could not delete components',
    text:
      'An error occurred while deleting the components. ' +
      'The error has been reported, but please contact support ' +
      'if the error persists.',
  });
  logError(error, 'confirmDeletion error');
};

type ConfirmDeleteBroadcastProps = {
  tagName: string;
  componentNames: string[];
  referenceNames: string[];
};

export const confirmDeleteTag = async ({
  tagName,
  componentNames,
  referenceNames,
}: ConfirmDeleteBroadcastProps) => {
  // i18nLocale$.state OK here: snapshot-at-open-time; @ardoq/modal renders confirm() body once and does not re-render on locale change
  const i18nLocale = i18nLocale$.state;
  return confirmDelete({
    title: t(
      i18nLocale,
      'confirmDeletion:tag.title',
      'Are you sure you want to delete the tag?'
    ),
    text: (
      <>
        <Trans
          i18nKey="confirmDeletion:tag.body"
          defaults="Are you sure you want to delete the <tagBold>#{{tagName}}</tagBold> tag and remove it from the following components and references:"
          values={{ tagName }}
          components={{ tagBold: <strong /> }}
        />
        <br />
        <br />
        <ul>
          {componentNames.map(componentName => (
            <li key={componentName}>{componentName}</li>
          ))}
          {referenceNames.map(referenceName => (
            <li key={referenceName}>{referenceName}</li>
          ))}
        </ul>
      </>
    ),
    confirmButtonClickId: 'confirm-delete-tag',
    modalSize: ModalSize.M,
  });
};
